import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { requestPasswordRecoveryEmail } from "../services/passwordRecoveryApi";
import "../style/AccountPage.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const INITIAL_HINT = "Inserisci l'email del tuo account Kyros Games per ricevere il link di reset.";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({
    kind: "muted",
    text: INITIAL_HINT,
  });

  const normalizedEmail = useMemo(
    () => String(email || "").trim().toLowerCase(),
    [email]
  );

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setStatus({
        kind: "error",
        text: "Inserisci un indirizzo email valido.",
      });
      return;
    }

    setBusy(true);
    try {
      await requestPasswordRecoveryEmail(normalizedEmail);
      setStatus({
        kind: "ok",
        text: "Ti abbiamo inviato una mail all'indirizzo specificato",
      });
    } catch (error) {
      setStatus({
        kind: "error",
        text: error.message || "Impossibile inviare la richiesta in questo momento.",
      });
    } finally {
      setBusy(false);
    }
  };

  const pagePath = "/account/forgot-password";
  const pageUrl = `https://kyrosgames.com${pagePath}`;

  return (
    <>
      <SEO
        title="Recupera Password | Account Kyros Games"
        description="Richiedi il link di reset password per il tuo account Kyros Games."
        canonical={pageUrl}
        robots="noindex, nofollow"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${pageUrl}#webpage`,
              url: pageUrl,
              name: "Recupera Password",
              inLanguage: "it-IT",
              isPartOf: { "@id": "https://kyrosgames.com/#website" },
              breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${pageUrl}#breadcrumb`,
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://kyrosgames.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Account",
                  item: "https://kyrosgames.com/account",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Recupera Password",
                  item: pageUrl,
                },
              ],
            },
          ],
        }}
      />

      <section className="account-page account-page--password-flow">
        <div className="account-page__inner">
          <header className="account-page__hero account-page__hero--auth">
            <span className="account-page__eyebrow">Account Recovery</span>
            <h1>Hai dimenticato la password?</h1>
            <p>
              Inserisci la tua email e ti invieremo un link per creare una nuova password.
            </p>
          </header>

          <section className="account-page__grid">
            <article className="account-card account-card--auth account-card--password-flow">
              <div className="account-page__brand-line">
                <span className="account-page__brand-dot" aria-hidden="true" />
                <strong>Kyros Games Account</strong>
              </div>
              <h2>Reset Password</h2>
              <p className="account-page__auth-switch">
                Ricordi la password? <Link to="/account">Torna al login</Link>
              </p>

              <form
                id="forgotPasswordForm"
                className="account-page__auth-form"
                noValidate
                onSubmit={onSubmit}
              >
                <div className="account-page__field-grid account-page__field-grid--single">
                  <div className="account-page__field">
                    <label htmlFor="forgotPasswordEmail">Email</label>
                    <input
                      id="forgotPasswordEmail"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="nome@dominio.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </div>
                </div>
                <p className="account-page__security-note">
                  Il link inviato via email scade dopo circa 15 minuti.
                </p>
                <div className="actions actions--stack">
                  <button
                    id="btnForgotPasswordContinue"
                    type="submit"
                    className="btn-primary"
                    disabled={busy}
                  >
                    {busy ? "Invio in corso..." : "Continua"}
                  </button>
                </div>
                <div className={`status account-page__status-block ${status.kind}`}>{status.text}</div>
              </form>
            </article>
          </section>
        </div>
      </section>
    </>
  );
}
